import { FilterOptionKeys, ID } from '@griply/types';

import {
  filterOptionKeysOrder,
  sortFilterOptionKeys,
} from './filter-menu-options';

export type FilterSectionDefinition = {
  id: ID;
  titleKey: string;
  keys: FilterOptionKeys[];
};

// The filters a task list offers on the goal a task belongs to.
export const taskListGoalFilterKeys: FilterOptionKeys[] = [
  FilterOptionKeys.Goal,
  FilterOptionKeys.GoalActive,
  FilterOptionKeys.GoalImpact,
  FilterOptionKeys.GoalType,
];

// Task lists: Date/Deadline are the task's own dates.
export const taskFilterSections: FilterSectionDefinition[] = [
  {
    id: 'lifeAreas',
    titleKey: 'context-menus.filtering.sections.life-areas.title',
    keys: [FilterOptionKeys.LifeArea],
  },
  {
    id: 'goals',
    titleKey: 'context-menus.filtering.sections.goals-and-projects.title',
    keys: taskListGoalFilterKeys,
  },
  {
    id: 'tasksAndHabits',
    titleKey: 'context-menus.filtering.sections.tasks-and-habits.title',
    keys: [
      FilterOptionKeys.Tag,
      FilterOptionKeys.Date,
      FilterOptionKeys.Deadline,
      FilterOptionKeys.CompletedAt,
      FilterOptionKeys.Priority,
      FilterOptionKeys.Duration,
      FilterOptionKeys.Type,
      FilterOptionKeys.Deleted,
    ],
  },
];

const goalsSectionId = 'goals';

// Goal lists: every filter targets a goal, Date/Deadline included.
export const goalFilterSections: FilterSectionDefinition[] = [
  {
    id: 'lifeAreas',
    titleKey: 'context-menus.filtering.sections.life-areas.title',
    keys: [FilterOptionKeys.LifeArea],
  },
  {
    id: goalsSectionId,
    titleKey: 'context-menus.filtering.sections.goals.title',
    keys: [
      FilterOptionKeys.Goal,
      FilterOptionKeys.GoalActive,
      FilterOptionKeys.GoalImpact,
      FilterOptionKeys.GoalType,
      FilterOptionKeys.Date,
      FilterOptionKeys.Deadline,
    ],
  },
];

// Same membership as a goal list; only what the section is called differs.
const withGoalSectionTitle = (titleKey: string): FilterSectionDefinition[] =>
  goalFilterSections.map((section) =>
    section.id === goalsSectionId ? { ...section, titleKey } : section,
  );

export const goalAndProjectFilterSections = withGoalSectionTitle(
  'context-menus.filtering.sections.goals-and-projects.title',
);

export const projectFilterSections = withGoalSectionTitle(
  'context-menus.filtering.sections.projects.title',
);

// Empty sections are dropped — a panel never renders a section title with no rows.
export const resolveFilterSections = (
  sections: FilterSectionDefinition[],
  supportedFilters?: readonly FilterOptionKeys[],
): FilterSectionDefinition[] => {
  const order = supportedFilters
    ? sortFilterOptionKeys(supportedFilters)
    : [...filterOptionKeysOrder];

  return sections.reduce<FilterSectionDefinition[]>((acc, section) => {
    const keys = order.filter((key) => section.keys.includes(key));
    if (!keys.length) {
      return acc;
    }
    acc.push({ ...section, keys });
    return acc;
  }, []);
};
